export const blogPosts = [
  {
    slug: 'how-to-choose-a-ux-design-agency',
    title: 'How to Choose a UX Design Agency: 8 Questions to Ask Before You Sign',
    excerpt:
      'Most agency pitches sound identical. Here are the questions that actually reveal how a UX design agency works, before you commit budget to find out the hard way.',
    category: 'Hiring a UX Agency',
    date: '2026-07-12',
    readTime: '7 min read',
    seoDescription:
      'How to choose a UX design agency: 8 direct questions to ask before signing, covering process, team, scope changes, ownership, and references. A practical guide for enterprise, fintech, and SaaS teams evaluating vendors.',
    seoKeywords:
      'how to choose a ux design agency, ui ux design agency, best ux design agencies, ux consulting companies, best ux companies, ui ux design firm',
    content: [
      {
        type: 'paragraph',
        text: 'Every UX agency website says roughly the same thing: research driven, design led, results focused. That language does not tell you anything about how a team actually works once you have signed a contract and money is moving. The questions below are the ones that do.',
      },
      {
        type: 'heading',
        text: '1. What happens in the first two weeks?',
      },
      {
        type: 'paragraph',
        text: 'A team that has run this process many times can answer this in specifics: a kickoff call, an access and context gathering step, a scoped plan with dates. A vague answer here, "it depends on the project", usually means the process gets figured out as they go, on your budget.',
      },
      {
        type: 'heading',
        text: '2. Who exactly will work on our project?',
      },
      {
        type: 'paragraph',
        text: 'Ask by name and seniority, not by title on a website. Some agencies sell a senior team on the pitch call and staff the actual work with junior contractors or subcontracted freelancers you never meet. That is not automatically bad, but you deserve to know before you sign, not after the first deliverable underwhelms.',
      },
      {
        type: 'heading',
        text: '3. Can you show a project outside your own industry?',
      },
      {
        type: 'paragraph',
        text: 'A portfolio full of one industry can mean deep specialization, or it can mean the agency only knows one playbook. Ask how they approached a product with fundamentally different constraints than yours. The answer tells you whether they have a process, or a template.',
      },
      {
        type: 'heading',
        text: '4. What does research actually look like in your process?',
      },
      {
        type: 'paragraph',
        text: 'Almost every agency claims to be research driven. Few can describe what that means in practice: how many users they talk to, how findings get synthesized, how research changes a design decision mid project. If the answer is vague, the research is probably vague too.',
      },
      {
        type: 'heading',
        text: '5. How do you handle scope changes?',
      },
      {
        type: 'paragraph',
        text: 'Scope always changes. The question is whether that is handled with a clear change process, or with quiet resentment and a rushed final deliverable. Ask what happens, specifically, when new requirements come up mid project.',
      },
      {
        type: 'heading',
        text: '6. What do we own at the end?',
      },
      {
        type: 'paragraph',
        text: 'Source files, Figma libraries, research recordings and notes, documentation. Some agencies hand over everything by default. Others treat their process and files as proprietary. Neither is wrong, but you need to know which one you are signing up for before the engagement ends and you are locked out of your own project.',
      },
      {
        type: 'heading',
        text: '7. What happens if it is not working?',
      },
      {
        type: 'paragraph',
        text: 'Ask about notice periods, exit terms, and what happens to work in progress if either side wants to stop. A team confident in their work will answer this directly instead of getting defensive.',
      },
      {
        type: 'heading',
        text: '8. Can we talk to a past client?',
      },
      {
        type: 'paragraph',
        text: 'Not a quote on a website. An actual conversation. Any agency worth hiring should be able to connect you with someone who worked with them directly, ideally on a project with some similarity to yours.',
      },
      {
        type: 'heading',
        text: 'The red flags that matter most',
      },
      {
        type: 'list',
        items: [
          'A timeline with no milestones, only a final delivery date.',
          'No mention of research or evidence anywhere in how they describe their process.',
          'Difficulty explaining their own process clearly and specifically, on the spot, without marketing language.',
          'Pricing that is either suspiciously vague or suspiciously rigid with zero room to discuss scope.',
        ],
      },
      {
        type: 'paragraph',
        text: 'We built our own engagement process to hold up against every question on this list, because we would ask the same ones before hiring anyone. You can see exactly how our project and retainer engagements work, including pricing, on our How We Work page.',
      },
    ],
  },
  {
    slug: 'designing-ai-features-users-actually-trust',
    title: 'Designing AI Features Users Actually Trust',
    excerpt:
      'AI features do not fail adoption because the model is bad. They fail because users cannot predict, verify, or control what the AI just did. Here is how to design around that.',
    category: 'AI UX',
    date: '2026-07-09',
    readTime: '7 min read',
    seoDescription:
      'A practical guide to AI UX design: why AI features fail to earn user trust, and five principles for designing AI powered products people actually adopt, from a team that helped build GitHub Copilot Enterprise support experiences.',
    seoKeywords:
      'ai ux design, ai product design, designing ai features, ai user experience, trust in ai products',
    content: [
      {
        type: 'paragraph',
        text: 'Most teams building AI features assume the hard problem is the model. Often it is not. The hard problem is that users cannot see how the AI arrived at an answer, cannot easily verify it, and cannot predict what it will do next. That uncertainty is exactly what kills adoption, no matter how good the underlying model actually is.',
      },
      {
        type: 'paragraph',
        text: 'We worked with GitHub to help design the support experience around Copilot Enterprise, which meant designing interfaces where AI output had to earn trust from people who did not write the model and had no reason to blindly believe it. A few principles came out of that work that apply to almost any AI feature.',
      },
      {
        type: 'heading',
        text: '1. Show your work',
      },
      {
        type: 'paragraph',
        text: 'If an AI feature makes a suggestion, users trust it more when they can see some reasoning behind it, not just the output. This does not mean exposing raw model internals. It means giving users enough context, a source, a confidence signal, a "based on" explanation, to judge whether the suggestion makes sense.',
      },
      {
        type: 'heading',
        text: '2. Make outputs easy to verify and edit',
      },
      {
        type: 'paragraph',
        text: 'Binary accept or reject buttons are a design shortcut, not a real solution. The most trusted AI features let users see exactly what will change, edit it before committing, and easily undo it after. Verification and editability do more for trust than accuracy claims ever will.',
      },
      {
        type: 'heading',
        text: '3. Fail visibly, not silently',
      },
      {
        type: 'paragraph',
        text: 'When an AI feature cannot complete a task, confidently guessing is worse than clearly saying so. A visible "I could not determine this" moment preserves trust. A silent wrong answer destroys it, usually permanently, because the user has no way to know which of the next ten answers to trust either.',
      },
      {
        type: 'heading',
        text: '4. Give users control over when AI runs',
      },
      {
        type: 'paragraph',
        text: 'AI that runs automatically in the background, without the user asking for it, tends to feel invasive even when it is technically helpful. Letting users trigger AI assistance on their own terms, and turn it off entirely, consistently increases adoption compared to always on automation.',
      },
      {
        type: 'heading',
        text: '5. Set expectations for imperfection up front',
      },
      {
        type: 'paragraph',
        text: 'Products that quietly imply their AI is always right set themselves up for a hard fall the first time it is not. Products that are upfront about limitations, this can make mistakes, please verify before relying on it for X, build a more durable kind of trust that survives the inevitable wrong answer.',
      },
      {
        type: 'heading',
        text: 'The pattern underneath all five',
      },
      {
        type: 'paragraph',
        text: 'Every one of these principles is really about control. Users do not need an AI feature to be perfect. They need to feel like they are still the one in charge of the outcome. Design for that, and adoption follows. Design around it, and even a genuinely capable model will get ignored.',
      },
      {
        type: 'paragraph',
        text: 'If you are building AI features and want a second set of eyes on where users might stop trusting the experience, that is exactly the kind of work we do for enterprise and SaaS product teams. You can see our design services or book a consultation to talk through what you are building.',
      },
    ],
  },
  {
    slug: 'enterprise-ux-vs-consumer-ux',
    title: 'Enterprise UX vs Consumer UX: Why the Same Playbook Does Not Work',
    excerpt:
      'A lot of enterprise software still gets designed with a consumer mindset bolted on top. It does not work, because enterprise users are optimizing for something completely different.',
    category: 'Enterprise UX',
    date: '2026-07-05',
    readTime: '6 min read',
    seoDescription:
      'Why enterprise UX design requires a different approach than consumer UX. A breakdown of five key differences, for enterprise, logistics, and B2B product teams designing complex applications.',
    seoKeywords:
      'enterprise ux, enterprise user experience, ux for enterprise applications, enterprise ux design, b2b ux design',
    content: [
      {
        type: 'paragraph',
        text: 'Consumer apps optimize for delight, because the user chose to download them and can delete them just as easily. Enterprise software does not get that luxury, and does not need it. Enterprise users did not choose the software. It was assigned to them, and they are using it whether they like it or not, forty hours a week, to get their actual job done. That single difference changes almost everything about how enterprise UX should be designed.',
      },
      {
        type: 'heading',
        text: '1. Efficiency beats delight',
      },
      {
        type: 'paragraph',
        text: 'A first time consumer user needs to be charmed. A daily enterprise user needs friction removed. A beautiful onboarding tour that a consumer app user sees once is, for an enterprise power user, an obstacle they will resent on day forty. Design for the fiftieth use, not the first.',
      },
      {
        type: 'heading',
        text: '2. The buyer and the user are different people',
      },
      {
        type: 'paragraph',
        text: 'Consumer products design for one person making one decision. Enterprise products have to satisfy a buyer, usually a director or VP focused on ROI and risk, and an end user, focused on getting through their day. Admin tools, permissions, and reporting exist because someone other than the daily user needs to see and control what is happening.',
      },
      {
        type: 'heading',
        text: '3. Data density is a feature, not a flaw',
      },
      {
        type: 'paragraph',
        text: 'Consumer design leans toward whitespace and simplicity. A logistics dispatcher tracking forty shipments, or a support agent reviewing account history, needs to see more information at once, not less. Stripping density out of an enterprise dashboard in the name of clean design often makes the tool slower to use, not easier.',
      },
      {
        type: 'heading',
        text: '4. Workflows cannot break',
      },
      {
        type: 'paragraph',
        text: 'A consumer app can ship an imperfect redesign and iterate based on feedback. An enterprise workflow that breaks for even a day can stop a support team, a billing team, or a warehouse from doing their job. Enterprise UX has to account for legacy integrations, edge case permissions, and the cost of downtime in a way consumer UX rarely does.',
      },
      {
        type: 'heading',
        text: '5. Change has to be managed, not just shipped',
      },
      {
        type: 'paragraph',
        text: 'Rolling out a new interface to a team that has used the old one for five years is a different problem than shipping a redesign to app store users. Enterprise UX work usually has to include a rollout plan, training material, and sometimes a way to toggle between old and new, because the resistance to change is organizational, not just personal preference.',
      },
      {
        type: 'heading',
        text: 'Why this matters for who you hire',
      },
      {
        type: 'paragraph',
        text: 'A lot of design talent comes up building consumer products, where the instincts above are actively counterproductive. That is not a knock on that talent, it is a mismatch of context. Enterprise UX is close to its own discipline, and it is worth hiring for that specifically rather than assuming good consumer design instincts transfer directly.',
      },
      {
        type: 'paragraph',
        text: 'Enterprise UX consulting is one of our core specialties, across fintech, logistics, and internal tooling. If you are wrestling with a product that has to serve both a buyer and a daily user without breaking either one, take a look at our strategy services or book a consultation.',
      },
    ],
  },
  {
    slug: 'designing-trust-into-fintech-products',
    title: 'Designing Trust Into Fintech Products: A UX Checklist for Regulated Industries',
    excerpt:
      'In fintech, a confusing screen does not just frustrate users, it looks like a bug that might have cost them money. A practical checklist for designing UX that holds up under real financial stakes.',
    category: 'FinTech UX',
    date: '2026-07-01',
    readTime: '6 min read',
    seoDescription:
      'A practical UX checklist for fintech and regulated financial products: system status, error messaging, compliance disclosures, and confirmation patterns that keep users confident their money is safe.',
    seoKeywords:
      'fintech ux design, fintech ui ux design, financial product ux, regulated industry ux, fintech design checklist',
    content: [
      {
        type: 'paragraph',
        text: 'Consumer apps can afford a confusing moment. A fintech product cannot. When a user is looking at their money, an ambiguous screen does not read as a minor UX issue, it reads as "did something just go wrong with my funds." That single difference in stakes should change almost every design decision in a financial product.',
      },
      {
        type: 'paragraph',
        text: 'We have designed customer and agent experiences for financial technology platforms handling real money and real compliance requirements. Here is the checklist we design against.',
      },
      {
        type: 'heading',
        text: 'Make system status impossible to misread',
      },
      {
        type: 'paragraph',
        text: 'Pending, processing, completed, failed. Every state a transaction can be in needs its own clear, distinct visual treatment. Users should never have to guess whether an action actually went through. When in doubt, show more status, not less.',
      },
      {
        type: 'heading',
        text: 'Never let an error message near money be vague',
      },
      {
        type: 'paragraph',
        text: '"Something went wrong" next to a transfer is one of the fastest ways to lose user trust permanently. Error messages touching money need to say specifically what happened, whether the money moved, and exactly what the user should do next.',
      },
      {
        type: 'heading',
        text: 'Design confirmation patterns for irreversible actions',
      },
      {
        type: 'paragraph',
        text: 'Transfers, withdrawals, and account changes deserve a deliberate confirmation step, not because users are careless, but because the cost of an accidental tap is real money. This is one of the few places in UX where adding a small amount of friction is the right call.',
      },
      {
        type: 'heading',
        text: 'Build compliance into the flow, not on top of it',
      },
      {
        type: 'paragraph',
        text: 'Disclosures, required fields, and regulatory language often get bolted onto a flow late, which is why they usually feel like a wall of legal text nobody reads. Designing compliance requirements in from the start, as part of the actual flow, produces something users are far more likely to actually understand.',
      },
      {
        type: 'heading',
        text: 'Treat accessibility as core, not optional',
      },
      {
        type: 'paragraph',
        text: 'Financial products serve every kind of user, including people relying on screen readers and keyboard navigation to manage their own money. In a regulated industry, accessibility gaps are not just a UX debt, they can become a real compliance and legal exposure.',
      },
      {
        type: 'heading',
        text: 'Show your math',
      },
      {
        type: 'paragraph',
        text: 'Fees, exchange rates, interest calculations. Whenever a number on screen came from a calculation, show the breakdown somewhere accessible. Users trust numbers they can trace far more than numbers they are simply asked to accept.',
      },
      {
        type: 'paragraph',
        text: 'Fintech UX design is one of our core specialties, built around exactly these constraints. If you are designing a financial product and want a partner who understands the stakes, take a look at our services or book a consultation.',
      },
    ],
  },
  {
    slug: 'saas-onboarding-ux-first-session',
    title: 'SaaS Onboarding UX: Why Most Products Lose Users in the First Session',
    excerpt:
      'Most SaaS churn does not happen because the product is bad. It happens because onboarding never gets the user to real value fast enough for them to find out.',
    category: 'SaaS UX',
    date: '2026-06-24',
    readTime: '6 min read',
    seoDescription:
      'Why SaaS onboarding fails, common patterns that kill activation, and what good onboarding UX does differently. A practical guide for SaaS product and design teams working on activation and retention.',
    seoKeywords:
      'saas ux design, saas onboarding ux, saas ui ux design, saas activation, saas ux',
    content: [
      {
        type: 'paragraph',
        text: 'Most SaaS teams treat churn as a retention problem to solve months into the customer relationship. Often the real problem happened in the first session, when a new user never got far enough to experience the thing that makes the product actually worth paying for.',
      },
      {
        type: 'heading',
        text: 'The patterns that kill activation',
      },
      {
        type: 'list',
        items: [
          'Too many steps before any real value appears, so users give up before they see what the product actually does.',
          'Generic product tours that explain UI elements nobody asked about yet, instead of guiding toward a specific outcome.',
          'Asking for setup information, team invites, integrations, before showing any payoff for providing it.',
          'No way to skip ahead or explore freely, forcing every user through the same rigid path regardless of what they actually came to do.',
          'Empty states that just say "nothing here yet" instead of showing the user exactly what to do next.',
        ],
      },
      {
        type: 'heading',
        text: 'What good onboarding does differently',
      },
      {
        type: 'paragraph',
        text: 'The best SaaS onboarding is built backward from one question: what is the fastest path to the moment this user understands why they need this product? Everything else, setup, invites, configuration, gets pushed to after that moment, not before it.',
      },
      {
        type: 'paragraph',
        text: 'That usually means using progressive disclosure instead of a full tour, showing one relevant piece of guidance at the exact moment it is needed rather than explaining the entire interface up front. It means treating empty states as an opportunity to guide action, a clear next step, a template, a sample, rather than a dead end. And it means contextual education, tooltips and prompts tied to what the user is actually doing, instead of a generic walkthrough delivered before they have any context to understand it.',
      },
      {
        type: 'heading',
        text: 'How to diagnose your own onboarding',
      },
      {
        type: 'paragraph',
        text: 'You do not need a full research study to find your biggest onboarding problem. Three signals usually point directly at it.',
      },
      {
        type: 'list',
        items: [
          'Time to first value: how long does it actually take a new user to reach the moment your product proves its worth.',
          'The step where most users drop off, visible in any basic funnel analytics.',
          'What new users ask support in their first week. Repeated questions about the same step are a direct signal of an onboarding gap, not a support training problem.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Fixing onboarding is rarely about adding more explanation. It is almost always about removing steps between signup and the moment a user understands why they should stay.',
      },
      {
        type: 'paragraph',
        text: 'SaaS UX design, including onboarding and activation work, is one of our core specialties. If you want a second set of eyes on where your product is losing new users, take a look at our design services or book a consultation.',
      },
    ],
  },
  {
    slug: 'how-to-run-a-ux-audit',
    title: 'How to Run a UX Audit: A Practical Framework for Enterprise Product Teams',
    excerpt:
      'A step by step framework for auditing your product’s usability, accessibility, and conversion friction, the same process we use with enterprise, fintech, and SaaS clients.',
    category: 'UX Audits',
    date: '2026-06-17',
    readTime: '8 min read',
    seoDescription:
      'A practical, step by step UX audit framework for enterprise, fintech, and SaaS product teams. Learn what to evaluate, how to structure findings, and how to avoid the mistakes that make internal audits fall flat.',
    seoKeywords:
      'how to run a ux audit, ux audit framework, ux audit checklist, ux audit process, enterprise ux audit, ux audit company',
    content: [
      {
        type: 'paragraph',
        text: "Most enterprise product teams don't commission a UX audit because they are curious. They commission one because something is already wrong: support tickets are climbing, activation rates have stalled, a big redesign is on the roadmap and nobody wants to guess where to start, or leadership is asking hard questions about a product that took years to build and still confuses new users in the first five minutes.",
      },
      {
        type: 'paragraph',
        text: "A UX audit is the fastest, lowest risk way to answer those questions with evidence instead of opinion. Here is the framework we use with enterprise, fintech, and SaaS clients, and how you can apply the same thinking whether you hire it out or run it internally.",
      },
      {
        type: 'heading',
        text: 'What a UX audit actually is (and is not)',
      },
      {
        type: 'paragraph',
        text: 'A UX audit is a structured, evidence based review of a product’s usability, accessibility, and conversion friction. It is not a redesign. It does not produce final mockups or a new design system. What it produces is a prioritized list of what is broken, why it matters to the business, and what to fix first. Think of it as a diagnostic, not a treatment plan, though a good audit will point clearly toward the treatment.',
      },
      {
        type: 'paragraph',
        text: 'Teams often confuse an audit with a casual "let’s all click around the app and share opinions in Slack" exercise. That is not an audit. Without a fixed scope, a consistent evaluation method, and a severity rating system, you end up with a pile of subjective feedback and no way to tell a five minute fix from a three month one.',
      },
      {
        type: 'heading',
        text: 'The four phase framework',
      },
      {
        type: 'paragraph',
        text: 'A UX audit that actually changes what a team builds next follows four phases: kickoff, evaluation, deliverable, and readout. Skipping any one of them is usually why internal audits stall out or get ignored.',
      },
      {
        type: 'heading',
        text: '1. Kickoff and scope',
      },
      {
        type: 'paragraph',
        text: 'Before anyone opens the product, agree in writing on which flows are in scope. Trying to audit an entire enterprise application in one pass is how audits become vague and unfinished. Pick the three to five flows that matter most, usually the ones tied to activation, retention, or revenue, and pull in whatever context already exists: analytics, support tickets, past research, churn data. This step alone prevents most of the scope creep that kills internal audits.',
      },
      {
        type: 'heading',
        text: '2. Evaluation',
      },
      {
        type: 'paragraph',
        text: 'This is the core of the audit, and it has three parts.',
      },
      {
        type: 'list',
        items: [
          'Heuristic evaluation: walk each in scope flow against established usability principles (visibility of system status, consistency, error prevention, and so on) and flag every point of friction.',
          'Accessibility pass: check against WCAG 2.1 AA, both automated scans and manual keyboard and screen reader testing. Automated tools catch maybe a third of real issues, so manual testing is not optional if you want the audit to hold up.',
          'Competitive benchmark: run the same flows against two or three competitors. This gives you a baseline for what "good" looks like in your category and helps you argue for investment when a finding is not just a preference, but a real gap against the market.',
        ],
      },
      {
        type: 'heading',
        text: '3. The deliverable',
      },
      {
        type: 'paragraph',
        text: 'This is where most internal audits fall apart. A pile of screenshots with comments is not a deliverable a VP can act on. A findings report that actually drives change has four parts.',
      },
      {
        type: 'list',
        items: [
          'An executive summary, one page, for the stakeholder who will never read the rest.',
          'Findings rated by severity (critical, high, medium, low), each with a screenshot, the issue, the recommendation, and a rough effort estimate.',
          'A quick wins versus strategic fixes split, so the team has something they can ship this sprint alongside the bigger bets.',
          'A closing recommendation or roadmap section that turns the findings into a plan, not just a list of complaints.',
        ],
      },
      {
        type: 'paragraph',
        text: 'Severity ratings matter more than most teams expect. Without them, a broken checkout flow and a slightly inconsistent button radius end up looking like the same size problem, and that is how audits lose credibility with engineering leadership.',
      },
      {
        type: 'heading',
        text: '4. The readout call',
      },
      {
        type: 'paragraph',
        text: 'Never just email the report. Walk stakeholders through the findings live, in the order that matches how they think about risk and roadmap, not the order the issues happened to be found. This is also where most audits actually convert into funded work, because it is the moment a VP sees a screen recording of a real user getting stuck on something they assumed was obvious.',
      },
      {
        type: 'heading',
        text: 'Common mistakes teams make auditing themselves',
      },
      {
        type: 'list',
        items: [
          'No fixed scope, so the audit balloons and never finishes.',
          'Mixing personal design opinions with evidence based findings, which makes the whole report easy to dismiss.',
          'Skipping severity ratings, so every issue reads as equally urgent.',
          'No follow up plan, so the report sits in a shared drive and nothing changes.',
        ],
      },
      {
        type: 'paragraph',
        text: 'The teams that get the most value out of a UX audit treat it as the first step of a larger conversation, not a one off document. If you want a second, outside set of eyes on your product using this exact framework, that is the audit we run for enterprise, fintech, AI, and SaaS teams. You can see what is included and book one on our services page.',
      },
    ],
  },
  {
    slug: 'how-much-does-a-ux-design-agency-cost',
    title: 'How Much Does a UX Design Agency Cost? A Real Breakdown',
    excerpt:
      'Most agency pricing pages hide the number until a sales call. Here is what UX design actually costs, broken into project work and ongoing retainers, with real ranges.',
    category: 'UX Agency Pricing',
    date: '2026-07-17',
    readTime: '6 min read',
    seoDescription:
      'How much does a UX design agency cost? A real breakdown of project pricing and monthly retainers, what drives the cost up or down, and how to compare quotes from different agencies.',
    seoKeywords:
      'ux design agency cost, ux agency pricing, how much does ux design cost, ux consulting pricing, hire ux design agency cost',
    content: [
      {
        type: 'paragraph',
        text: 'Most UX agencies will not put a number on their website. You have to book a call, sit through a pitch, and wait for a proposal before you find out if the price is even in your range. That wastes your time and theirs. Here is an honest breakdown of what UX design actually costs, and what makes the number go up or down.',
      },
      {
        type: 'heading',
        text: 'The two ways agencies price work',
      },
      {
        type: 'paragraph',
        text: 'Almost every UX agency prices work one of two ways: a fixed scope project, or a monthly retainer. Understanding which one fits your situation matters more than chasing the lowest number, because the two models solve different problems.',
      },
      {
        type: 'heading',
        text: 'Project pricing: roughly $4,000 to $40,000',
      },
      {
        type: 'paragraph',
        text: 'A project engagement covers one specific piece of work with a clear start and end, a UX audit, a research sprint, or a focused redesign of one flow. Pricing is agreed before work starts, so you know the total cost upfront instead of getting billed by the hour. Simple audits and small research sprints tend to land on the lower end of that range. A full redesign of a complex product touching multiple flows costs more, because it takes more senior hours to do well.',
      },
      {
        type: 'heading',
        text: 'Retainer pricing: roughly $8,000 to $20,000 a month',
      },
      {
        type: 'paragraph',
        text: 'A retainer is ongoing, ordinarily priced by how many hours of dedicated design time you need each week. This model fits teams that already have designers but need more capacity, or teams under a hiring freeze who still have a full roadmap to ship. Retainers typically run month to month, so you are not locked into a long contract, and most stay in place for six months or longer once the working relationship proves out.',
      },
      {
        type: 'heading',
        text: 'What actually drives the price up',
      },
      {
        type: 'list',
        items: [
          'Seniority of the team. A senior designer with ten years of enterprise experience costs more per hour than a junior one, and usually gets to a better answer faster, which can make the total cost similar or lower.',
          'How many people touch the project. A single senior point of contact is often cheaper and faster than a rotating account team with a project manager, a researcher, and a designer all billing separately.',
          'Research included or not. Agencies that fold user research into the price, instead of quoting it separately, tend to charge more upfront but deliver decisions backed by evidence instead of opinion.',
          'Industry complexity. Fintech, healthcare, and other regulated industries take longer to design correctly because of compliance and accessibility requirements, which shows up in the quote.',
        ],
      },
      {
        type: 'heading',
        text: 'Why an agency retainer often costs less than a hire',
      },
      {
        type: 'paragraph',
        text: 'A designer at a $10,000 a month salary is not actually $10,000 a month once you add payroll tax, benefits, a recruiter fee, and the months of an open job posting and ramp time before they ship anything real. A retainer skips almost all of that. It usually comes out of a different budget line than headcount too, so it does not need the same internal approval a new hire does. If you already have a design team and just need more hands, a retainer is often the cheaper and faster option, not just the more flexible one.',
      },
      {
        type: 'heading',
        text: 'How to compare quotes from different agencies',
      },
      {
        type: 'paragraph',
        text: 'Do not just compare the bottom line number. Ask what is actually included: how many rounds of revisions, whether research is part of the price or billed separately, who exactly does the work and at what seniority, and what you own once the engagement ends. Two quotes that look far apart on price can end up costing the same once you account for what is missing from the cheaper one.',
      },
      {
        type: 'paragraph',
        text: 'Pegasus UX runs project engagements starting at $4,000 and monthly retainers starting at $12,000, both with a fixed scope agreed before work begins. You can see exactly what is included in each on our how we work page, or book a consultation to get a real quote for your project.',
      },
    ],
  },
  {
    slug: 'in-house-design-team-vs-ux-agency',
    title: 'In House Design Team vs UX Agency: How to Decide',
    excerpt:
      'Hiring a designer and hiring an agency solve different problems. Here is how to tell which one your team actually needs right now, without guessing.',
    category: 'Hiring a UX Agency',
    date: '2026-07-22',
    readTime: '6 min read',
    seoDescription:
      'In house UX design team versus hiring a UX agency: a practical guide to deciding which fits your product team right now, covering cost, speed, and long term fit.',
    seoKeywords:
      'in house vs agency ux design, should i hire a ux designer or agency, ux design team vs agency, hiring ux designer',
    content: [
      {
        type: 'paragraph',
        text: 'This question comes up on almost every discovery call we run. Teams assume it has to be one or the other, hire a designer or hire an agency, when the honest answer is usually that they solve different problems. Getting this decision wrong either way costs months.',
      },
      {
        type: 'heading',
        text: 'When an in house hire is the right call',
      },
      {
        type: 'paragraph',
        text: 'If you have zero design capacity and need a permanent, embedded team member who deeply understands your product over years, hire. A great in house designer builds context an outside partner never fully gets, sits in your team meetings, and grows with the product long term. If design is core to your company and you can afford to recruit, ramp, and retain someone for the long haul, an in house hire is usually the better long term investment.',
      },
      {
        type: 'heading',
        text: 'When an agency is the right call',
      },
      {
        type: 'paragraph',
        text: 'An agency fits when you need capacity now, not in three months after a hiring process finishes. It also fits when the problem is specific and time bound, an audit, a research sprint, a redesign, rather than an ongoing need for a permanent team member. Teams under a hiring freeze but still expected to ship a full roadmap are the clearest case, since the budget for a new hire is not there but the work still has to happen.',
      },
      {
        type: 'heading',
        text: 'The real cost comparison',
      },
      {
        type: 'paragraph',
        text: 'A designer at a $120,000 salary is closer to $150,000 once you add payroll tax and benefits, before counting recruiter fees or the months of an open job posting and ramp time before they ship real work. An agency retainer skips the recruiting process and the ramp time entirely, and it usually comes out of a different budget line than headcount, so it can move faster through internal approval. That does not make an agency automatically cheaper over a full year, but it does mean the true first year cost of a hire is almost always higher than the number on the offer letter.',
      },
      {
        type: 'heading',
        text: 'Speed to first output',
      },
      {
        type: 'list',
        items: [
          'Hiring a designer: usually two to four months from opening the role to their first shipped work, once you count sourcing, interviews, notice periods, and onboarding.',
          'Engaging an agency: usually one to two weeks from first call to work actually starting, since there is no recruiting process in between.',
        ],
      },
      {
        type: 'heading',
        text: 'The hybrid answer most teams land on',
      },
      {
        type: 'paragraph',
        text: 'Most product teams that scale end up doing both, an in house team that owns the product long term, supplemented by an agency for overflow capacity, specialized work like a UX audit or a fintech compliance heavy flow, or a deadline that recruiting cannot meet in time. Treat the two as complementary tools instead of competing options, and the decision gets a lot easier.',
      },
      {
        type: 'paragraph',
        text: 'If you already have designers and just need more capacity, or you are trying to figure out which model fits your team, that is exactly the conversation we have on a discovery call. Take a look at how our engagements work, or book a consultation.',
      },
    ],
  },
  {
    slug: 'ux-design-for-logistics-software',
    title: 'UX Design for Logistics Software: What Dispatchers and Brokers Actually Need',
    excerpt:
      'Logistics software gets designed for demos, not for the person matching drivers to loads at 6am. Here is what actually matters when the user is under real time pressure.',
    category: 'Logistics UX',
    date: '2026-07-29',
    readTime: '6 min read',
    seoDescription:
      'A practical guide to UX design for logistics and freight software, covering what dispatchers, brokers, and drivers actually need from an interface, drawn from a real logistics platform redesign.',
    seoKeywords:
      'logistics ux design, freight software ux, logistics software design, dispatch software ux, trucking software design',
    content: [
      {
        type: 'paragraph',
        text: 'Logistics software has a specific kind of user that most design advice does not account for. A broker matching drivers to loads is not casually browsing an app. They are working against the clock, juggling multiple deals at once, and every extra click costs them a load to a competitor. Designing for that person is a different job than designing a consumer app.',
      },
      {
        type: 'paragraph',
        text: 'We led a four phase redesign of Transport Pro, a logistics platform connecting drivers, load managers, and brokers, that was still running on a codebase from the early 2000s. Here is what we learned about designing for this kind of user.',
      },
      {
        type: 'heading',
        text: 'Speed to the answer beats visual polish',
      },
      {
        type: 'paragraph',
        text: 'A broker deciding whether a driver can take a load needs the answer in seconds: location, availability, equipment type, rate. Burying that information behind a click, a tab, or a modal costs real money when a competing platform shows it faster. Prioritize getting the critical fields on screen immediately over making the screen look clean.',
      },
      {
        type: 'heading',
        text: 'Dense data is not a design failure',
      },
      {
        type: 'paragraph',
        text: 'A dispatcher tracking dozens of active loads needs to see many of them at once, not one card at a time behind a scroll. Stripping information out of a logistics dashboard in the name of simplicity usually makes the tool slower to use, because the user just has to click into more screens to find what used to be visible at a glance.',
      },
      {
        type: 'heading',
        text: 'The interface is part of the sales pitch',
      },
      {
        type: 'paragraph',
        text: 'In a competitive logistics market, brokers and shippers judge a platform partly on how modern it feels, even when the underlying matching logic is just as good as a competitor. An outdated interface can lose new business before a prospect ever tests the actual functionality, simply because it reads as an old, unreliable system.',
      },
      {
        type: 'heading',
        text: 'What we prioritized in the Transport Pro redesign',
      },
      {
        type: 'list',
        items: [
          'Simplified the navigation so key actions were no longer buried in a nested header menu.',
          'Redesigned the driver matching flow so brokers could scan availability faster instead of digging through dense tables.',
          'Kept the information density experienced users relied on, while cleaning up the visual noise around it.',
          'Modernized the visual design so the platform read as current to prospective customers evaluating it against competitors.',
        ],
      },
      {
        type: 'heading',
        text: 'The results',
      },
      {
        type: 'paragraph',
        text: 'The redesign drove a two fold increase in user retention and a twenty percent or greater improvement in how well users could match drivers to available loads, along with a double digit percentage increase in companies choosing Transport Pro as their logistics platform. User feedback consistently pointed to a smoother path from opening the app to completing a task, with far less time lost navigating dense screens.',
      },
      {
        type: 'paragraph',
        text: 'Logistics and SaaS platforms with real operational complexity are one of our core specialties. You can read the full Transport Pro case study, or book a consultation if you are working on a logistics or dispatch platform that needs the same kind of attention.',
      },
    ],
  },
  {
    slug: 'when-your-product-needs-a-design-system',
    title: 'When Your Enterprise Product Actually Needs a Design System',
    excerpt:
      'Not every product needs a design system, and building one too early can slow a team down instead of speeding it up. Here is how to tell when the investment is actually worth it.',
    category: 'Design Systems',
    date: '2026-08-04',
    readTime: '6 min read',
    seoDescription:
      'A practical guide to deciding when your enterprise or SaaS product actually needs a design system, the signs you are ready, and the signs you are not.',
    seoKeywords:
      'enterprise design system, when to build a design system, design system for saas, do we need a design system',
    content: [
      {
        type: 'paragraph',
        text: 'Design systems are treated as a default best practice, something every serious product team should have. In reality, a design system built too early costs a team real time for very little payoff, and a design system that never gets built for a large, sprawling product costs a team even more, just more slowly and less visibly.',
      },
      {
        type: 'heading',
        text: 'Signs you are ready for one',
      },
      {
        type: 'list',
        items: [
          'More than one designer is working on the product, and their work is starting to look inconsistent without one of them noticing until QA or a user does.',
          'Engineers are rebuilding the same button, form field, or modal slightly differently in different parts of the product.',
          'The product has grown past a handful of core screens into dozens of flows, and a redesign of any single component now means finding and fixing it in five different places.',
          'You are onboarding new designers or engineers regularly, and they have no single source of truth for how components should look or behave.',
        ],
      },
      {
        type: 'heading',
        text: 'Signs you are not ready yet',
      },
      {
        type: 'list',
        items: [
          'You have one designer and a small, still changing product. A design system built now will likely need to be rebuilt once the product direction settles.',
          'Your team is pre product market fit and still figuring out what the product even is. Consistency matters far less than speed at this stage.',
          'Nobody on the team has bandwidth to maintain the system once it exists. An unmaintained design system decays fast and becomes actively misleading within a few months.',
        ],
      },
      {
        type: 'heading',
        text: 'What a design system actually needs to include',
      },
      {
        type: 'paragraph',
        text: 'A design system is not just a color palette and a component library in Figma. To actually hold up, it needs documented usage rules for when to use each component, accessibility standards built into each one, a clear versioning and update process, and buy in from engineering to actually build against it instead of treating it as a suggestion. A beautiful component library that engineering ignores is not a design system, it is a reference document nobody follows.',
      },
      {
        type: 'heading',
        text: 'Start smaller than you think',
      },
      {
        type: 'paragraph',
        text: 'Teams often try to build a comprehensive design system in one big effort before shipping any of it. A better approach is to start with the components used most often, buttons, form inputs, navigation, and typography, ship those, and expand the system as real needs come up. A design system that grows from actual product needs tends to get used. One built all at once, disconnected from real screens, tends to drift out of date fast.',
      },
      {
        type: 'paragraph',
        text: 'Design systems work is one of the things we do as part of both project and retainer engagements for enterprise and SaaS teams. If you are trying to figure out whether your product is ready for one, take a look at our design services or book a consultation to talk through where your product actually stands.',
      },
    ],
  },
  {
    slug: 'ux-research-methods-for-enterprise-teams',
    title: 'UX Research Methods That Actually Work for Enterprise Teams',
    excerpt:
      'Enterprise research runs into problems consumer research does not: hard to reach users, compliance restrictions, and stakeholders who want an answer in a week. Here is what actually works.',
    category: 'UX Research',
    date: '2026-08-08',
    readTime: '7 min read',
    seoDescription:
      'A practical guide to UX research methods for enterprise product teams, covering how to reach hard to access users, work around compliance restrictions, and get research into decisions faster.',
    seoKeywords:
      'enterprise ux research, ux research methods, b2b user research, ux research for enterprise software, enterprise user research',
    content: [
      {
        type: 'paragraph',
        text: 'Most UX research advice is written for consumer products, where you can recruit a stranger off the street and pay them fifty dollars for an hour of their time. Enterprise research does not work that way. The users are hard to reach, their time is expensive, and half the time a compliance team has opinions about what you are even allowed to ask them.',
      },
      {
        type: 'heading',
        text: 'The access problem, and how to work around it',
      },
      {
        type: 'paragraph',
        text: 'Enterprise users are often internal employees at your client companies, or specialists like brokers, dispatchers, or claims adjusters who cannot easily step away from their job for an hour long research session. Recruiting through your sales or customer success team, rather than a generic research panel, usually gets faster and better access, since those teams already have relationships with the right people. Short, focused fifteen to twenty minute sessions also get a much higher yes rate than the standard sixty minute consumer research block.',
      },
      {
        type: 'heading',
        text: 'Methods that work well for enterprise products',
      },
      {
        type: 'list',
        items: [
          'Contextual inquiry: watching someone do their actual job with the product, not a scripted task. Enterprise workflows are often too specialized to simulate accurately in a lab setting.',
          'Stakeholder interviews alongside end user interviews. In enterprise software, the buyer and the daily user are frequently different people with different goals, and research needs to cover both.',
          'Diary studies for infrequent tasks. Some enterprise workflows only happen monthly or quarterly, like a compliance report or an audit, and a single interview will not capture the real pain points that only show up during the actual task.',
          'Analytics paired with interviews, not analytics alone. Enterprise usage data can show where users drop off, but only a conversation reveals why, especially when the drop off is caused by an offline step like getting manager approval.',
        ],
      },
      {
        type: 'heading',
        text: 'Working around compliance and legal restrictions',
      },
      {
        type: 'paragraph',
        text: 'In regulated industries like fintech and healthcare, legal and compliance teams often need to review research plans before anyone talks to a real user, and recordings may need to be handled carefully or avoided entirely. Build this review into your timeline from the start instead of discovering it after a study is already scheduled. A synthetic or sandboxed environment, using realistic but non real data, can also let you run usability tests without touching regulated information at all.',
      },
      {
        type: 'heading',
        text: 'Getting research into decisions, not just a report',
      },
      {
        type: 'paragraph',
        text: 'The biggest failure mode in enterprise research is not bad methodology, it is research that gets read once and never changes a decision. Tie every research effort to a specific decision that is actually being made, a redesign, a prioritization call, a go or no go on a feature, rather than running research as a general exercise. When stakeholders can see the direct line from what a user said to what the team decided to build, research earns a bigger seat at the table next time.',
      },
      {
        type: 'paragraph',
        text: 'Research is one of our core services, folded into both audits and ongoing retainer work for enterprise and fintech teams. If you need help getting real evidence in front of a decision your team is stuck on, take a look at our research services or book a consultation.',
      },
    ],
  },
  {
    slug: 'web-accessibility-checklist-for-enterprise-software',
    title: 'Web Accessibility for Enterprise Software: A Practical WCAG Checklist',
    excerpt:
      'Accessibility gaps in enterprise software are not just a UX problem, they are a legal and compliance risk. Here is a practical checklist to actually close the gaps that matter most.',
    category: 'Accessibility',
    date: '2026-08-13',
    readTime: '7 min read',
    seoDescription:
      'A practical WCAG 2.1 AA accessibility checklist for enterprise and fintech software teams, covering keyboard navigation, screen readers, color contrast, and how to avoid common compliance gaps.',
    seoKeywords:
      'web accessibility checklist, wcag checklist, enterprise accessibility, wcag 2.1 aa, accessible ux design',
    content: [
      {
        type: 'paragraph',
        text: 'Accessibility gets treated as a nice to have in a lot of enterprise product roadmaps, something to circle back to after the next feature ships. In regulated industries and large enterprise contracts, that is a mistake. Accessibility gaps are increasingly a legal exposure, and enterprise buyers now regularly ask vendors for a VPAT before signing. Here is a practical checklist, not a legal document, to close the gaps that matter most.',
      },
      {
        type: 'heading',
        text: 'Keyboard navigation',
      },
      {
        type: 'list',
        items: [
          'Every interactive element, buttons, links, form fields, dropdowns, can be reached and operated using only a keyboard, no mouse.',
          'Focus order follows a logical reading order, not a random order based on how the code happens to be structured.',
          'A visible focus indicator shows clearly which element is currently focused, at all times.',
          'No keyboard trap exists where a user can tab into a component, like a modal, and cannot tab back out.',
        ],
      },
      {
        type: 'heading',
        text: 'Screen reader support',
      },
      {
        type: 'list',
        items: [
          'Every image that conveys information has meaningful alt text, and purely decorative images are marked so screen readers skip them.',
          'Form fields have properly associated labels, not just placeholder text that disappears once a user starts typing.',
          'Error messages are announced to screen readers when they appear, not just shown visually in red text.',
          'Dynamic content updates, like a loading state finishing or a new item being added, are announced through ARIA live regions where appropriate.',
        ],
      },
      {
        type: 'heading',
        text: 'Color and contrast',
      },
      {
        type: 'list',
        items: [
          'Text meets a contrast ratio of at least 4.5 to 1 against its background for normal text, and 3 to 1 for large text, per WCAG 2.1 AA.',
          'Color is never the only way information is conveyed. A red versus green status indicator also needs a label or icon, for users with color blindness.',
          'Interface states, hover, focus, disabled, remain distinguishable for users with low vision, not just for users with typical color vision.',
        ],
      },
      {
        type: 'heading',
        text: 'Forms and errors',
      },
      {
        type: 'paragraph',
        text: 'Enterprise software is full of dense, complex forms, which makes this category especially high risk. Every required field should be clearly marked, not just implied by a small asterisk buried in fine print. Error messages need to say specifically what is wrong and how to fix it, and should be programmatically linked to the field they refer to so a screen reader user is not left guessing which field has the problem.',
      },
      {
        type: 'heading',
        text: 'Why automated scans are not enough',
      },
      {
        type: 'paragraph',
        text: 'Automated accessibility scanning tools are a useful first pass, but they typically catch somewhere around a third of real issues. Problems like a confusing tab order, a screen reader announcing something in a misleading way, or a focus trap in a custom component almost always require manual testing with an actual keyboard and screen reader to catch. Treat automated scans as a floor, not a finish line.',
      },
      {
        type: 'paragraph',
        text: 'We build accessibility into audits and design work as a standard part of our process for enterprise and fintech clients, not an optional add on. If you need a real accessibility review of your product, take a look at our UX audit service or book a consultation.',
      },
    ],
  },
  {
    slug: 'how-to-redesign-a-legacy-enterprise-product',
    title: 'How to Redesign a Legacy Enterprise Product Without Breaking It',
    excerpt:
      'Redesigning software that thousands of people rely on every day is not the same job as designing something new. Here is how to modernize a legacy product without breaking the workflows people depend on.',
    category: 'Enterprise UX',
    date: '2026-08-16',
    readTime: '7 min read',
    seoDescription:
      'A practical framework for redesigning legacy enterprise software without disrupting the daily workflows users depend on, covering phased rollouts, change management, and how to avoid the most common mistakes.',
    seoKeywords:
      'legacy software redesign, enterprise product redesign, modernizing legacy software, ux redesign process, enterprise ui modernization',
    content: [
      {
        type: 'paragraph',
        text: 'Redesigning a product that new users are still discovering is one kind of project. Redesigning a product that thousands of people have used every single day for years, sometimes on a codebase over a decade old, is a completely different one. The interface might look outdated, but it is also deeply familiar to the people whose job depends on it, and a redesign that ignores that fact tends to get rejected by the exact users it was meant to help.',
      },
      {
        type: 'heading',
        text: 'Start by mapping what people actually do, not what the product claims to do',
      },
      {
        type: 'paragraph',
        text: "Legacy products accumulate workarounds. Users develop habits, keyboard shortcuts, and unofficial processes that never show up in a product spec but are essential to how they actually get their job done. Before redesigning anything, watch real users complete real tasks. A redesign that removes a workaround without understanding why it existed will break someone's daily workflow, even if the new design looks cleaner on a screen.",
      },
      {
        type: 'heading',
        text: 'Phase the rollout instead of shipping a full replacement',
      },
      {
        type: 'paragraph',
        text: 'Replacing an entire interface overnight is one of the fastest ways to spike support tickets and lose trust with your user base. A phased approach, redesigning one flow at a time, or offering a toggle between the old and new interface for a transition period, gives users time to adjust and gives your team time to catch problems before they affect everyone at once.',
      },
      {
        type: 'heading',
        text: 'Preserve information density where it matters',
      },
      {
        type: 'paragraph',
        text: 'A common mistake in legacy redesigns is applying modern, minimal design trends to a tool built for power users managing dense information. A dispatcher, an account manager, or a claims adjuster who is used to seeing forty rows of data on one screen will not thank you for a cleaner looking interface that now requires five extra clicks to see the same information. Modernize the visual design, but keep the density experienced users actually rely on.',
      },
      {
        type: 'heading',
        text: 'Build a real change management plan',
      },
      {
        type: 'list',
        items: [
          'Give advance notice before any major interface change ships, not a surprise on login.',
          'Provide short, specific training material tied to the actual changes, not a generic product tour.',
          'Identify power users early and get their feedback before a wider rollout, since they will surface problems fastest.',
          'Keep a clear feedback channel open during rollout so issues get caught and fixed quickly, not weeks later.',
        ],
      },
      {
        type: 'heading',
        text: 'What this looked like in practice',
      },
      {
        type: 'paragraph',
        text: 'When we led the redesign of Transport Pro, a logistics platform running on a codebase from the early 2000s, we worked through the interface in four phases rather than one full replacement. The result was a two fold increase in user retention and a twenty percent or greater improvement in how effectively users could match drivers to available loads, without the disruption a full overnight replacement usually causes.',
      },
      {
        type: 'paragraph',
        text: 'Modernizing legacy enterprise software without breaking what already works is one of our core specialties. You can read the full Transport Pro case study, or book a consultation if you are facing a legacy redesign of your own.',
      },
    ],
  },
  {
    slug: 'ux-design-for-internal-tools',
    title: 'UX Design for Internal Tools: Why It Keeps Getting Ignored',
    excerpt:
      'Internal tools run entire companies, yet they are almost always the last thing to get real design attention. Here is why that keeps happening, and what it actually costs.',
    category: 'Internal Tools UX',
    date: '2026-08-20',
    readTime: '6 min read',
    seoDescription:
      'Why internal tools rarely get proper UX design attention, what that actually costs a business in lost productivity, and a practical approach to designing internal software employees can actually use.',
    seoKeywords:
      'internal tools ux design, internal tool design, employee software ux, admin panel design, internal dashboard ux',
    content: [
      {
        type: 'paragraph',
        text: 'Every company has at least one internal tool that everyone complains about and nobody fixes. A support dashboard, an admin panel, an internal reporting tool. These tools run entire teams, yet they are almost always the last thing to get real design attention, and the gap between how good they are and how good customer facing software has become keeps growing every year.',
      },
      {
        type: 'heading',
        text: 'Why internal tools get ignored',
      },
      {
        type: 'paragraph',
        text: 'The reasoning is usually the same at every company. Internal tools do not generate revenue directly, so they lose the prioritization fight against customer facing features every single roadmap cycle. Employees are also seen as a captive audience who will use the tool no matter how bad it is, since it is part of their job. Both of those assumptions are technically true and also badly wrong about what they actually cost the business.',
      },
      {
        type: 'heading',
        text: 'What a bad internal tool actually costs',
      },
      {
        type: 'list',
        items: [
          'Time. If five hundred employees lose ten minutes a day to a confusing internal tool, that is over eighty hours of lost productivity every single day across the company.',
          'Errors. A confusing admin interface leads to real mistakes, wrong data entered, wrong account modified, that can cost far more to fix than the tool would have cost to design properly.',
          'Turnover. New employees judge a company partly by the tools they are handed on day one. A frustrating internal tool is a small but real contributor to poor onboarding experiences and early attrition.',
          'Support load. Internal help desk tickets about "how do I do X" in an internal tool are a direct, measurable signal that the interface is not doing its job.',
        ],
      },
      {
        type: 'heading',
        text: 'What good internal tool design actually requires',
      },
      {
        type: 'paragraph',
        text: 'Internal tools do not need the same visual polish as a customer facing product, but they need something arguably more important: efficiency for a repeat user who touches the tool dozens of times a day. That means dense information layouts over generous whitespace, keyboard shortcuts for frequent actions, and clear, fast error recovery when something goes wrong. Design for the fiftieth use, not the first impression, since almost nobody discovers an internal tool for the first time and churns.',
      },
      {
        type: 'heading',
        text: 'A practical starting point',
      },
      {
        type: 'paragraph',
        text: 'You rarely need to redesign every internal tool a company owns. Start with whichever one generates the most support tickets or the most complaints in an all hands meeting. That is usually the tool costing the most in hidden productivity loss, and a focused redesign of just that one tool tends to make the strongest case internally for investing in the rest.',
      },
      {
        type: 'paragraph',
        text: 'Internal tooling is part of the enterprise design work we do for clients, alongside customer facing product work. If your team has an internal tool everyone complains about but nobody has fixed, take a look at our UX audit service or book a consultation to talk through where to start.',
      },
    ],
  },
  {
    slug: 'ux-metrics-that-matter-to-executives',
    title: 'UX Metrics That Actually Matter to Your Executive Team',
    excerpt:
      "Design teams often measure the wrong things when it is time to prove UX work is worth the investment. Here are the metrics that actually change an executive team's mind.",
    category: 'UX Strategy',
    date: '2026-08-23',
    readTime: '6 min read',
    seoDescription:
      'A practical guide to the UX metrics that actually influence executive decisions, why design satisfaction scores rarely move the needle, and how to tie UX work to revenue and retention.',
    seoKeywords:
      'ux metrics for executives, measuring ux roi, ux design metrics that matter, proving ux value, ux kpi',
    content: [
      {
        type: 'paragraph',
        text: 'Design teams asking for more budget or headcount often lead with the wrong metrics. A high design satisfaction score or a positive usability testing session feels like proof that UX work matters, but it rarely moves an executive who is thinking in revenue, retention, and cost. Here is what actually lands in that conversation.',
      },
      {
        type: 'heading',
        text: 'Metrics executives actually care about',
      },
      {
        type: 'list',
        items: [
          'Activation rate. The percentage of new users or customers who reach real value in the product, since this is a direct leading indicator of revenue and churn.',
          'Time to value. How long it takes a new user to experience the thing that makes your product worth paying for. Shorter time to value consistently correlates with lower churn.',
          'Support ticket volume tied to specific flows. A spike in tickets about one screen is a measurable cost that a redesign can directly reduce.',
          'Task completion rate on revenue critical flows, like checkout, signup, or a core conversion action. Even a small improvement here has a direct, calculable dollar value.',
          'Retention and expansion revenue. For enterprise and SaaS products, whether users stick around and grow their usage over time is the metric that ultimately funds everything else.',
        ],
      },
      {
        type: 'heading',
        text: 'Why satisfaction scores alone do not convince executives',
      },
      {
        type: 'paragraph',
        text: 'A survey saying users rate the product 4.2 out of 5 for ease of use is a real signal, but it does not translate into a dollar figure an executive can put in a budget conversation. Satisfaction scores are useful internally for tracking whether design changes are moving in the right direction, but they rarely carry weight in a conversation about where to invest limited resources.',
      },
      {
        type: 'heading',
        text: 'How to connect UX work to a business metric',
      },
      {
        type: 'paragraph',
        text: 'Before starting a redesign or a research project, identify the specific business metric it is meant to move, and measure that metric before and after the change ships. A checkout redesign should be measured by conversion rate, not just usability scores. An onboarding redesign should be measured by activation rate and week one retention. This takes more discipline upfront, but it is the only way to build a track record that earns design a real seat in prioritization conversations.',
      },
      {
        type: 'heading',
        text: 'The reporting mistake to avoid',
      },
      {
        type: 'paragraph',
        text: 'Do not wait until a project is finished to figure out how you will measure it. Decide the target metric and how you will track it during the scoping phase of the project, before any design work starts. Trying to reconstruct impact after the fact from incomplete analytics is a common reason good UX work never gets credited with the results it actually produced.',
      },
      {
        type: 'paragraph',
        text: 'Tying design and research work to measurable business outcomes is core to how we run every engagement, project or retainer. If you need help building the case for UX investment inside your organization, take a look at our strategy services or book a consultation.',
      },
    ],
  },
  {
    slug: 'how-to-give-design-feedback-that-works',
    title: 'How to Give Design Feedback That Actually Improves the Product',
    excerpt:
      'Most design feedback is either too vague to act on or so specific it turns into someone redesigning the work themselves. Here is how to give feedback that actually makes the product better.',
    category: 'Design Process',
    date: '2026-08-25',
    readTime: '6 min read',
    seoDescription:
      'A practical guide to giving effective design feedback in product teams, covering common mistakes, how to separate opinion from evidence, and a simple framework for critique that actually improves the work.',
    seoKeywords:
      'how to give design feedback, design critique framework, design review process, giving ux feedback, product design critique',
    content: [
      {
        type: 'paragraph',
        text: 'Nearly every product team has design reviews, and most of them are not very good. Feedback either arrives so vague it cannot be acted on, "this does not feel right", or so specific and prescriptive that it turns into the reviewer redesigning the screen themselves instead of the actual designer. Neither version makes the product better.',
      },
      {
        type: 'heading',
        text: 'Separate opinion from evidence',
      },
      {
        type: 'paragraph',
        text: 'The most useful feedback distinguishes between "I personally do not like this" and "this will likely confuse users, and here is why." Personal taste is not irrelevant, but it should be labeled as taste, not delivered with the same authority as a usability concern grounded in research or a known pattern. A reviewer who is honest about which kind of feedback they are giving lets the designer weigh it correctly.',
      },
      {
        type: 'heading',
        text: 'Give feedback on the problem, not just the solution',
      },
      {
        type: 'paragraph',
        text: 'Telling a designer to move a button, change a color, or reword a label is feedback on one specific solution. It is usually more useful to describe the problem you see, "I am not sure a first time user will understand what this action does", and let the designer, who understands the full context of the screen, decide how to solve it. This produces better solutions and keeps ownership of the design where it belongs.',
      },
      {
        type: 'heading',
        text: 'A simple framework for structured critique',
      },
      {
        type: 'list',
        items: [
          'Start with what is working. This is not just politeness, it tells the designer what to keep as the work evolves, which matters as much as what to change.',
          'Ask questions before giving directives. "What was the thinking behind this layout?" often surfaces context that changes the feedback entirely.',
          'Separate must fix issues from nice to have suggestions. Treating every piece of feedback as equally urgent makes it impossible to prioritize revisions.',
          'End with a clear next step. Vague feedback with no resolution just creates another round of the same conversation later.',
        ],
      },
      {
        type: 'heading',
        text: 'Common mistakes that derail a design review',
      },
      {
        type: 'list',
        items: [
          'Too many stakeholders giving feedback in the same session, which often produces contradictory direction the designer cannot reconcile.',
          'Feedback delivered late in the process, after a direction is nearly finished, instead of during early concepts when changes are cheap.',
          'Debating feedback in the room instead of writing it down, so decisions get lost or re-litigated in the next meeting.',
          'Design reviews that never reference the actual goal of the screen, turning into a conversation about preferences instead of whether the design solves the problem.',
        ],
      },
      {
        type: 'heading',
        text: 'Why this matters more than it seems',
      },
      {
        type: 'paragraph',
        text: 'A team with a strong feedback culture ships better design work faster, because designers are not spending cycles guessing what stakeholders actually meant or defending decisions instead of improving them. This is a process problem, not a talent problem, and it is one of the fastest things a team can fix without hiring anyone new.',
      },
      {
        type: 'paragraph',
        text: 'Running a healthy design review process is part of how we work embedded inside client teams on retainer engagements. If your team wants a second set of eyes on how design decisions get made, not just the decisions themselves, take a look at our how we work page or book a consultation.',
      },
    ],
  },
];

export function getBlogPost(slug) {
  return blogPosts.find((post) => post.slug === slug);
}
